import { useCallback, useEffect, useRef, useState } from 'react';

export interface SyncRunLogEntry {
  id: string;
  layer_id: string;
  run_started_at: string;
  run_completed_at: string | null;
  source_to_target_count: number;
  target_to_source_count: number;
  error_count: number;
  errors_json: string[];
  triggered_by: 'cron' | 'manual';
}

export interface SyncLayerStatus {
  id: string;
  layer_id: string;
  board_id: string;
  is_enabled: boolean;
  provider: string;
  target_url: string | null;
  sync_interval_ms: number;
  field_map_json: Record<string, unknown>;
  arcgis_refresh_token_enc: string | null;
  arcgisConnected: boolean | null;
  lastRun: SyncRunLogEntry | null;
  created_at: string;
  updated_at: string;
}

interface UseSyncStatusResult {
  status: SyncLayerStatus | null;
  history: SyncRunLogEntry[];
  isLoading: boolean;
  refetch: () => void;
}

const POLL_INTERVAL_MS = 30_000;

export function useSyncStatus(layerId: string): UseSyncStatusResult {
  const [status, setStatus] = useState<SyncLayerStatus | null>(null);
  const [history, setHistory] = useState<SyncRunLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const layerIdRef = useRef(layerId);
  layerIdRef.current = layerId;
  const hasLoadedOnceRef = useRef(false);

  const fetchData = useCallback(async () => {
    const id = layerIdRef.current;
    if (!id) return;

    const showLoading = !hasLoadedOnceRef.current;
    if (showLoading) setIsLoading(true);

    try {
      const [statusRes, historyRes] = await Promise.all([
        fetch(`/api/sync/layers/${id}/status`),
        fetch(`/api/sync/layers/${id}/history`),
      ]);

      if (statusRes.ok) {
        const data = (await statusRes.json()) as SyncLayerStatus | null;
        setStatus(data);
      }
      if (historyRes.ok) {
        const data = (await historyRes.json()) as SyncRunLogEntry[];
        setHistory(data);
      }
      hasLoadedOnceRef.current = true;
    } catch {
      // Network error — keep previous state
    } finally {
      if (showLoading) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    hasLoadedOnceRef.current = false;
    setStatus(null);
    setHistory([]);
    void fetchData();

    const intervalId = window.setInterval(() => {
      void fetchData();
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [layerId, fetchData]);

  const refetch = useCallback(() => fetchData(), [fetchData]);

  return { status, history, isLoading, refetch };
}
